import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Home, DoorOpen, X } from 'lucide-react';
import axios from 'axios';
import { isFinanceAccount, currentAccount } from '@/lib/accountPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RoomManagement = () => {
  const { propertyName, ownerEmail } = useParams();
  const navigate = useNavigate();
  const canDelete = !isFinanceAccount(currentAccount());
  
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showAddRoomType, setShowAddRoomType] = useState(false);
  
  // Form states
  const [newRoom, setNewRoom] = useState({
    room_number: '',
    room_type: ''
  });
  const [newRoomType, setNewRoomType] = useState({
    name: '',
    price_per_month: ''
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [propertiesRes, roomsRes, roomTypesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/properties`, config),
        axios.get(`${BACKEND_URL}/api/admin/rooms?property_name=${encodeURIComponent(propertyName)}&owner_email=${encodeURIComponent(ownerEmail)}`, config),
        axios.get(`${BACKEND_URL}/api/admin/room-types?property_name=${encodeURIComponent(propertyName)}&owner_email=${encodeURIComponent(ownerEmail)}`, config)
      ]);
      
      const prop = propertiesRes.data.find(p => p.name === propertyName && p.owner_email === ownerEmail);
      setProperty(prop);
      setRooms(roomsRes.data);
      setRoomTypes(roomTypesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRoom = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newRoom.room_number.trim()) {
      setError('Nomor kamar harus diisi');
      return;
    }

    if (!newRoom.room_type) {
      setError('Pilih tipe kamar');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(
        `${BACKEND_URL}/api/admin/rooms?property_name=${encodeURIComponent(propertyName)}&owner_email=${encodeURIComponent(ownerEmail)}`,
        {
          room_number: newRoom.room_number,
          room_type: newRoom.room_type,
          status: 'available'
        },
        config
      );

      setSuccess('Kamar berhasil ditambahkan!');
      fetchData();
      setNewRoom({ room_number: '', room_type: '' });
      setTimeout(() => {
        setSuccess('');
        setShowAddRoom(false);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal menambah kamar');
    }
  };

  const handleAddRoomType = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newRoomType.name.trim()) {
      setError('Nama tipe kamar harus diisi');
      return;
    }

    if (!newRoomType.price_per_month) {
      setError('Harga per bulan harus diisi');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(
        `${BACKEND_URL}/api/admin/room-types?property_name=${encodeURIComponent(propertyName)}&owner_email=${encodeURIComponent(ownerEmail)}`,
        {
          name: newRoomType.name,
          price_per_month: parseFloat(newRoomType.price_per_month)
        },
        config
      );

      setSuccess('Tipe kamar berhasil ditambahkan!');
      fetchData();
      setNewRoomType({ name: '', price_per_month: '' });
      setTimeout(() => {
        setSuccess('');
        setShowAddRoomType(false);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal menambah tipe kamar');
    }
  };

  const handleDeleteRoom = async (roomNumber, roomStatus) => {
    if (!canDelete) return;
    let confirmMessage = `Hapus kamar ${roomNumber}?`;
    if (roomStatus === 'occupied') {
      confirmMessage = `⚠️ Kamar ${roomNumber} sedang TERISI!\n\nApakah Anda yakin ingin menghapus kamar ini?\nData tenant akan tetap tersimpan di riwayat.`;
    }
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.delete(
        `${BACKEND_URL}/api/admin/rooms/${roomNumber}?property_name=${encodeURIComponent(propertyName)}&owner_email=${encodeURIComponent(ownerEmail)}`,
        config
      );

      setSuccess('Kamar berhasil dihapus!');
      fetchData();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal menghapus kamar');
    }
  };

  const handleDeleteRoomType = async (typeName) => {
    if (!canDelete) return;
    if (!window.confirm(`Hapus tipe kamar "${typeName}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.delete(
        `${BACKEND_URL}/api/admin/room-types/${encodeURIComponent(typeName)}?property_name=${encodeURIComponent(propertyName)}&owner_email=${encodeURIComponent(ownerEmail)}`,
        config
      );

      setSuccess('Tipe kamar berhasil dihapus!');
      fetchData();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal menghapus tipe kamar');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const occupiedRooms = rooms.filter(r => r.status === 'occupied');
  const availableRooms = rooms.filter(r => r.status === 'available');

  if (loading) {
    return (
      <div className="min-h-screen bg-isk-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-isk-muted">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-isk-bg">
      {/* Header */}
      <div className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            className="mb-4 border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Admin
          </Button>
          
          <div>
            <h1 className="text-3xl font-bold text-isk-text">{property?.name}</h1>
            <p className="text-isk-muted mt-1">{property?.location} • Kelola Kamar</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-card rounded-2xl p-6" data-testid="total-rooms-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-isk-teal/10 flex items-center justify-center">
                <Home className="w-6 h-6 text-isk-teal" />
              </div>
            </div>
            <p className="text-sm text-isk-muted mb-1">Total Kamar</p>
            <p className="text-2xl font-bold text-isk-text">{rooms.length}</p>
          </div>

          <div className="glass-card rounded-2xl p-6" data-testid="occupied-rooms-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <DoorOpen className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-sm text-isk-muted mb-1">Kamar Terisi</p>
            <p className="text-2xl font-bold text-red-600">{occupiedRooms.length}</p>
          </div>

          <div className="glass-card rounded-2xl p-6" data-testid="available-rooms-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                <DoorOpen className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-isk-muted mb-1">Kamar Tersedia</p>
            <p className="text-2xl font-bold text-green-600">{availableRooms.length}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Room Types Section */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Tipe Kamar</h3>
              <Button
                onClick={() => setShowAddRoomType(true)}
                className="bg-isk-teal hover:bg-isk-dark text-white rounded-full"
                data-testid="add-room-type-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Tipe
              </Button>
            </div>

            {roomTypes.length > 0 ? (
              <div className="space-y-3">
                {roomTypes.map((type, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 rounded-xl bg-white/50 border border-white/20"
                    data-testid={`room-type-${index}`}
                  >
                    <div>
                      <p className="font-semibold text-isk-text">{type.name}</p>
                      <p className="text-sm text-isk-teal font-medium">{formatCurrency(type.price_per_month)}/bulan</p>
                    </div>
                    {canDelete && <Button
                      onClick={() => handleDeleteRoomType(type.name)}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-isk-muted">
                <DoorOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Belum ada tipe kamar</p>
                <p className="text-sm">Tambahkan tipe kamar terlebih dahulu</p>
              </div>
            )}
          </div>

          {/* Rooms Section */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Daftar Kamar</h3>
              <Button
                onClick={() => setShowAddRoom(true)}
                className="bg-isk-teal hover:bg-isk-dark text-white rounded-full"
                disabled={roomTypes.length === 0}
                data-testid="add-room-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Kamar
              </Button>
            </div>

            {rooms.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                {rooms.map((room, index) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-xl border-2 ${
                      room.status === 'occupied' 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-green-50 border-green-200'
                    }`}
                    data-testid={`room-${room.room_number}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-isk-text">{room.room_number}</span>
                      {canDelete && <Button
                        onClick={() => handleDeleteRoom(room.room_number, room.status)}
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>}
                    </div>
                    <p className="text-xs text-isk-muted">{room.room_type}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      room.status === 'occupied' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {room.status === 'occupied' ? 'Terisi' : 'Tersedia'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-isk-muted">
                <Home className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Belum ada kamar</p>
                <p className="text-sm">
                  {roomTypes.length === 0 
                    ? 'Tambahkan tipe kamar terlebih dahulu' 
                    : 'Klik tombol "Tambah Kamar" untuk menambah'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Add Multiple Rooms */}
        {roomTypes.length > 0 && (
          <div className="mt-8 glass-card rounded-2xl p-6">
            <h3 className="text-xl font-bold text-isk-text mb-4">Tambah Banyak Kamar Sekaligus</h3>
            <p className="text-sm text-isk-muted mb-4">
              Masukkan nomor kamar dipisahkan koma. Contoh: 101, 102, 103, 201, 202
            </p>
            <BulkAddRooms 
              propertyName={propertyName}
              ownerEmail={ownerEmail}
              roomTypes={roomTypes}
              onSuccess={() => {
                fetchData();
                setSuccess('Kamar berhasil ditambahkan!');
                setTimeout(() => setSuccess(''), 2000);
              }}
              onError={(msg) => setError(msg)}
            />
          </div>
        )}
      </div>

      {/* Modal Add Room Type */}
      {showAddRoomType && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
          <div className="glass-card rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Tambah Tipe Kamar</h3>
              <button onClick={() => setShowAddRoomType(false)} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddRoomType} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="type_name">Nama Tipe *</Label>
                <Input
                  id="type_name"
                  type="text"
                  required
                  value={newRoomType.name}
                  onChange={(e) => setNewRoomType({ ...newRoomType, name: e.target.value })}
                  placeholder="Contoh: Standard, Deluxe, Suite"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="input-room-type-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type_price">Harga per Bulan (Rp) *</Label>
                <Input
                  id="type_price"
                  type="number"
                  required
                  min="0"
                  value={newRoomType.price_per_month}
                  onChange={(e) => setNewRoomType({ ...newRoomType, price_per_month: e.target.value })}
                  placeholder="Contoh: 2000000"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="input-room-type-price"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowAddRoomType(false)}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full font-semibold"
                  data-testid="submit-room-type"
                >
                  Simpan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Add Room */}
      {showAddRoom && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
          <div className="glass-card rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Tambah Kamar</h3>
              <button onClick={() => setShowAddRoom(false)} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddRoom} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="room_number">Nomor Kamar *</Label>
                <Input
                  id="room_number"
                  type="text"
                  required
                  value={newRoom.room_number}
                  onChange={(e) => setNewRoom({ ...newRoom, room_number: e.target.value })}
                  placeholder="Contoh: 101, A1, Lt2-01"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="input-room-number"
                />
              </div>

              <div className="space-y-2">
                <Label>Tipe Kamar *</Label>
                <Select
                  value={newRoom.room_type}
                  onValueChange={(value) => setNewRoom({ ...newRoom, room_type: value })}
                >
                  <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="select-room-type">
                    <SelectValue placeholder="Pilih tipe kamar" />
                  </SelectTrigger>
                  <SelectContent>
                    {roomTypes.map((type, idx) => (
                      <SelectItem key={idx} value={type.name}>
                        {type.name} - {formatCurrency(type.price_per_month)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowAddRoom(false)}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full font-semibold"
                  data-testid="submit-room"
                >
                  Simpan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Component for bulk adding rooms
const BulkAddRooms = ({ propertyName, ownerEmail, roomTypes, onSuccess, onError }) => {
  const [roomNumbers, setRoomNumbers] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleBulkAdd = async () => {
    if (!roomNumbers.trim()) {
      onError('Masukkan nomor kamar');
      return;
    }
    if (!selectedType) {
      onError('Pilih tipe kamar');
      return;
    }

    const numbers = roomNumbers.split(',').map(n => n.trim()).filter(n => n);
    if (numbers.length === 0) {
      onError('Format nomor kamar tidak valid');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Add rooms one by one
      let successCount = 0;
      let errorCount = 0;
      
      for (const num of numbers) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/admin/rooms?property_name=${encodeURIComponent(propertyName)}&owner_email=${encodeURIComponent(ownerEmail)}`,
            {
              room_number: num,
              room_type: selectedType,
              status: 'available'
            },
            config
          );
          successCount++;
        } catch {
          errorCount++;
        }
      }

      if (successCount > 0) {
        onSuccess();
        setRoomNumbers('');
      }
      
      if (errorCount > 0) {
        onError(`${errorCount} kamar gagal ditambahkan (mungkin sudah ada)`);
      }
    } catch (err) {
      onError('Gagal menambah kamar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="flex-1">
        <Input
          type="text"
          value={roomNumbers}
          onChange={(e) => setRoomNumbers(e.target.value)}
          placeholder="101, 102, 103, 201, 202..."
          className="glass-input border-2 focus:border-isk-teal rounded-xl"
          data-testid="bulk-room-numbers"
        />
      </div>
      <div className="w-full sm:w-48">
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="bulk-room-type">
            <SelectValue placeholder="Tipe Kamar" />
          </SelectTrigger>
          <SelectContent>
            {roomTypes.map((type, idx) => (
              <SelectItem key={idx} value={type.name}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        onClick={handleBulkAdd}
        disabled={loading}
        className="bg-isk-teal hover:bg-isk-dark text-white rounded-full px-6"
        data-testid="bulk-add-btn"
      >
        {loading ? 'Menambah...' : 'Tambah Semua'}
      </Button>
    </div>
  );
};

export default RoomManagement;
